import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getMe, type MeResponse } from './widget-api/auth-me';
import { listMyBookings } from './widget-api/bookings';
import { permissionsListForEffectiveGuard } from './widget-api/auth-permissions';
import {
  fetchGlobalWidgetConfig,
  fetchPublicOrganizationsWithBranches,
  fetchPublicWidgetConfig
} from './widget-api/public-widget';
import { useAsync } from './widget-hooks/useAsync';
import { useEmbedSession } from './widget-hooks/use-embed-session';
import { EmbedBootstrapContext, type EmbedBootstrapValue } from './widget-bootstrap-context';
import { isUpcomingUnpaidBooking } from './widget-lib/booking-status';
import { ensureDeviceIdForAuth } from './widget-lib/device-id-bridge';
import { syncOfflineCacheInBackground } from './widget-lib/offline-cache';
import { resolveWidgetContour } from './widget-lib/widget-contour';

/**
 * Похоже ли значение на профиль `MeResponse`, а не на что-то ещё.
 * Нужно, чтобы `reloadMe`, переданный ссылкой (например `onClick={reloadMe}`),
 * не записал SyntheticEvent клика в `me` — React передаёт событие первым аргументом.
 */
function isMeResponseLike(value: unknown): value is MeResponse {
  if (value == null || typeof value !== 'object') return false;
  if ('nativeEvent' in value) return false;
  return typeof (value as MeResponse).id === 'number';
}

function parseBranchId(raw: string | null): number | null {
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function parseGuestOrgId(searchParams: URLSearchParams): number | null {
  const raw = searchParams.get('guest_org_id')?.trim();
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** `org_slugs=a,b` (и повторяющийся параметр) → нормализованный список без дублей. */
export function parseOrgSlugs(searchParams: URLSearchParams): string[] {
  const raw = searchParams.getAll('org_slugs');
  const out: string[] = [];
  for (const chunk of raw) {
    for (const part of chunk.split(',')) {
      const slug = part.trim();
      if (slug && !out.includes(slug)) out.push(slug);
    }
  }
  return out;
}

export function EmbedBootstrapProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const orgSlugs = useMemo(() => {
    const parsed = parseOrgSlugs(searchParams);
    if (parsed.length > 0) return parsed;
    const single = searchParams.get('org_slug')?.trim() || searchParams.get('org')?.trim();
    return single ? [single] : [];
  }, [searchParams]);

  const orgSlug = useMemo(() => {
    const a = searchParams.get('org_slug')?.trim();
    const b = searchParams.get('org')?.trim();
    // Одна студия из `org_slugs` — тот же бренд-режим, что и `org_slug`.
    return a || b || (orgSlugs.length === 1 ? orgSlugs[0] : null) || null;
  }, [searchParams, orgSlugs]);

  const branchId = useMemo(() => parseBranchId(searchParams.get('branch_id')), [searchParams]);

  const key = searchParams.get('key')?.trim() ?? '';
  const kid = searchParams.get('kid')?.trim() ?? '';
  const exp = searchParams.get('exp')?.trim() ?? '';
  const sig = searchParams.get('sig')?.trim() ?? '';

  const embedGlobal = searchParams.get('embed_global') === '1';
  const guestOrgIdParam = useMemo(() => parseGuestOrgId(searchParams), [searchParams]);
  // Глобальный конфиг нужен при любом `embed_global=1`, пока не режим guest_org_id: после выбора
  // организации в URL появляется org_slug, а GET /public/widget/{slug} без key/signed URL не вызывается —
  // иначе обнулялись бы и виджет, и widget-global → «слетала» тёмная тема до загрузки org-конфига.
  const fetchGlobalBootstrap = Boolean(embedGlobal);

  const canFetchWidget = Boolean(orgSlug && (key || (kid && exp && sig)));

  const shouldResolveGuestShowcase = Boolean(
    (embedGlobal && guestOrgIdParam != null && !canFetchWidget) ||
    (orgSlug != null && !canFetchWidget)
  );

  const { data: widgetConfig, loading, error, reload } = useAsync(
    () => {
      if (!orgSlug || !canFetchWidget) {
        return Promise.resolve(null);
      }
      const q: Record<string, string> = {};
      if (key) q.key = key;
      if (kid) q.kid = kid;
      if (exp) q.exp = exp;
      if (sig) q.sig = sig;
      return fetchPublicWidgetConfig(orgSlug, q);
    },
    [orgSlug, canFetchWidget, key, kid, exp, sig]
  );

  const {
    data: globalWidgetConfig,
    loading: globalWidgetLoading,
    error: globalWidgetError,
    reload: reloadGlobalWidget
  } = useAsync(
    () => {
      if (!fetchGlobalBootstrap) return Promise.resolve(null);
      return fetchGlobalWidgetConfig();
    },
    [fetchGlobalBootstrap]
  );

  const {
    data: guestShowcaseOrg,
    loading: guestShowcaseLoading,
    error: guestShowcaseError,
    reload: reloadGuestShowcase
  } = useAsync(
    async () => {
      if (!shouldResolveGuestShowcase) {
        return Promise.resolve(null);
      }
      const list = await fetchPublicOrganizationsWithBranches();
      if (guestOrgIdParam != null) {
        const found = list.find((o) => o.id === guestOrgIdParam);
        // Непубличная / удалённая организация — не ошибка API, а «недоступно»: даём странице показать fallback.
        return found ?? null;
      } else if (orgSlug) {
        const found = list.find((o) => o.slug === orgSlug);
        return found ?? null;
      }
      return null;
    },
    [shouldResolveGuestShowcase, guestOrgIdParam, orgSlug]
  );

  const effectiveOrgSlug = useMemo(() => {
    return orgSlug || guestShowcaseOrg?.slug || null;
  }, [orgSlug, guestShowcaseOrg]);

  const session = useEmbedSession(effectiveOrgSlug);

  useEffect(() => {
    void import('./widget-lib/embed-auth-bridge').then((m) => m.registerEmbedAuthBridge(effectiveOrgSlug));
    return () => {
      void import('./widget-lib/embed-auth-bridge').then((m) => m.registerEmbedAuthBridge(null));
    };
  }, [effectiveOrgSlug]);

  const bootstrapHint = useMemo(() => {
    if (!effectiveOrgSlug && orgSlugs.length === 0) {
      if (embedGlobal) {
        if (guestOrgIdParam != null) {
          return null;
        }
        return 'Глобальный виджет: выберите студию в каталоге или откройте персональную ссылку. Расписание без входа; запись и оплата — после авторизации.';
      }
      return 'Укажите в URL параметр org_slug (slug организации). Для данных виджета без входа добавьте key или kid/exp/sig; после входа расписание доступно без ключа.';
    }
    // Несколько студий в одном приложении: бренд-конфиг одной студии не обязателен.
    if (orgSlugs.length > 1) {
      return null;
    }
    if (!canFetchWidget && !session?.accessToken && !shouldResolveGuestShowcase) {
      return 'Для загрузки конфигурации виджета укажите key или параметры kid, exp, sig (signed URL). Либо войдите на странице «Вход», если уже есть аккаунт.';
    }
    return null;
  }, [effectiveOrgSlug, orgSlugs, embedGlobal, canFetchWidget, session?.accessToken, shouldResolveGuestShowcase, guestOrgIdParam]);

  const accessToken = session?.accessToken ?? null;
  const bookingsOrganizationId = widgetConfig?.organization_id ?? session?.organizationId ?? null;
  const {
    data: meRaw,
    loading: meLoadingRaw,
    error: meErrorRaw,
    reload: reloadMeRaw,
    setData: setMeData
  } = useAsync(
    async () => {
      if (!accessToken) return null;
      await ensureDeviceIdForAuth();
      return getMe(accessToken, effectiveOrgSlug);
    },
    [accessToken, effectiveOrgSlug]
  );

  /**
   * Обновление me в общем состоянии. `reloadMe(fresh)` записывает уже полученный профиль
   * (например, после выбора студии суперадмином) — вызывающий код может сразу переходить,
   * не дожидаясь повторного GET /auth/me и не попадая под старый guard.
   *
   * Второй рубеж защиты: если первым аргументом пришло не похожее на `MeResponse` значение
   * (SyntheticEvent из `onClick={reloadMe}`), ведём себя как `reloadMe()` — перезапрашиваем профиль.
   * Обработчики всё равно передаём обёрткой: `onClick={() => reloadMe()}`.
   */
  const reloadMe = useCallback(
    (next?: MeResponse) => {
      if (isMeResponseLike(next)) {
        setMeData(next);
        return;
      }
      reloadMeRaw();
    },
    [reloadMeRaw, setMeData]
  );

  const me = accessToken ? meRaw : null;
  const meLoading = Boolean(accessToken) && meLoadingRaw;
  const meError = accessToken ? meErrorRaw : null;
  const capabilitiesRaw = me?.capabilities ?? null;
  /** Preset-список (как в ответе API) — для отладки/совместимости. */
  const permissions = useMemo(() => capabilitiesRaw?.permissions ?? [], [capabilitiesRaw?.permissions]);
  /** Проверки hasPermission: используем снимок capabilities из GET /auth/me. */
  const permissionsForEffectiveGuard = useMemo(
    () => permissionsListForEffectiveGuard(capabilitiesRaw),
    [capabilitiesRaw]
  );
  const effectiveModules = useMemo(() => {
    const widgetRaw = widgetConfig?.widget;
    const widgetEff = (widgetRaw?.effective_modules as Record<string, boolean> | undefined) ?? (widgetRaw?.modules as Record<string, boolean> | undefined);
    if (widgetEff && Object.keys(widgetEff).length > 0) return widgetEff;
    const meEff = capabilitiesRaw?.modules_effective ?? me?.modules;
    if (meEff && Object.keys(meEff).length > 0) return meEff;
    return null;
  }, [widgetConfig?.widget, capabilitiesRaw?.modules_effective, me?.modules]);
  const permissionsLoading = meLoading;
  const permissionsError = meError;

  const { data: bookings, reload: reloadBookings } = useAsync(
    async () => {
      if (!accessToken) return null;
      return listMyBookings(accessToken, bookingsOrganizationId);
    },
    [accessToken, bookingsOrganizationId]
  );

  const unpaidBookingsCount = useMemo(() => {
    if (!bookings) return 0;
    const now = Date.now();
    return bookings.filter((b) => isUpcomingUnpaidBooking(b, now)).length;
  }, [bookings]);

  useEffect(() => {
    if (accessToken && me) {
      void syncOfflineCacheInBackground(accessToken, me, effectiveOrgSlug);
    }
  }, [accessToken, me, effectiveOrgSlug]);

  const value = useMemo((): EmbedBootstrapValue => {
    const effectiveGuestShowcaseOrg = guestShowcaseOrg ?? null;

    let organizationId: number | null =
      widgetConfig?.organization_id ?? session?.organizationId ?? null;
    if ((organizationId == null || organizationId < 1) && effectiveGuestShowcaseOrg != null) {
      organizationId = effectiveGuestShowcaseOrg.id;
    }
    const embedMode = session?.embedMode ?? 'client';
    // ?wn= — override от LAB/тестирования и мобильных приложений; иначе канал приложения или widget_name.
    const widgetContour = resolveWidgetContour({
      wnParam: searchParams.get('wn'),
      widgetChannel: searchParams.get('widget_channel'),
      pathname: location.pathname,
      widgetName: widgetConfig?.widget_name ?? null,
      hasGuestShowcaseOrg: effectiveGuestShowcaseOrg != null
    });
    const brandEmbedAllowed = widgetConfig != null ? widgetConfig.brand_embed_allowed : null;
    const widgetLoading = Boolean(canFetchWidget) && loading;
    const globalLoading = Boolean(fetchGlobalBootstrap) && globalWidgetLoading;
    const guestLoading = Boolean(shouldResolveGuestShowcase) && guestShowcaseLoading;
    const guestShowcaseBrowse = Boolean(!accessToken && effectiveGuestShowcaseOrg != null);

    const needsWidgetBootstrap = Boolean(canFetchWidget) && widgetConfig == null;
    const needsGlobalBootstrap = Boolean(fetchGlobalBootstrap) && globalWidgetConfig == null;
    // Резолв guest-showcase считается завершённым, когда запрос к каталогу отработал (found или нет) —
    // иначе «ненайденная организация» вечно держала бы скелетон.
    const needsGuestBootstrap = guestLoading;
    const awaitingBootstrapData = needsWidgetBootstrap || needsGlobalBootstrap || needsGuestBootstrap;
    const bootstrapGateError = Boolean(error || globalWidgetError || guestShowcaseError);
    const shellBootstrapLoader = awaitingBootstrapData && !bootstrapGateError;

    return {
      orgSlug: effectiveOrgSlug,
      orgSlugs,
      branchId,
      widgetConfig,
      globalWidgetConfig: globalWidgetConfig ?? null,
      brandEmbedAllowed,
      organizationId,
      guestShowcaseOrg: effectiveGuestShowcaseOrg,
      guestShowcaseLoading: guestLoading,
      guestShowcaseError: guestShowcaseError ?? null,
      guestShowcaseBrowse,
      embedMode,
      widgetContour,
      accessToken,
      me,
      meLoading,
      meError,
      reloadMe,
      permissions,
      effectiveModules,
      permissionsLoading,
      permissionsError,
      hasPermission: (permissionCode: string) => permissionsForEffectiveGuard.includes(permissionCode),
      loading: widgetLoading || globalLoading || guestLoading,
      shellBootstrapLoader,
      error: error ?? globalWidgetError ?? guestShowcaseError,
      reload: () => {
        reload();
        reloadGlobalWidget();
        reloadGuestShowcase();
        reloadBookings();
      },
      bootstrapHint,
      canFetchWidget,
      unpaidBookingsCount,
      reloadBookings
    };
  }, [
    searchParams,
    location.pathname,
    effectiveOrgSlug,
    orgSlugs,
    branchId,
    widgetConfig,
    globalWidgetConfig,
    globalWidgetLoading,
    globalWidgetError,
    guestShowcaseOrg,
    guestShowcaseLoading,
    guestShowcaseError,
    shouldResolveGuestShowcase,
    session?.organizationId,
    session?.embedMode,
    accessToken,
    me,
    meLoading,
    meError,
    reloadMe,
    permissions,
    effectiveModules,
    permissionsForEffectiveGuard,
    permissionsLoading,
    permissionsError,
    loading,
    error,
    reload,
    reloadGlobalWidget,
    reloadGuestShowcase,
    bootstrapHint,
    canFetchWidget,
    fetchGlobalBootstrap,
    unpaidBookingsCount,
    reloadBookings
  ]);

  return <EmbedBootstrapContext.Provider value={value}>{children}</EmbedBootstrapContext.Provider>;
}
